import { useState } from 'react';

import { useUpdateProfessionalRate } from '../hooks/useAdminProfessionals';
import type { AdminProfessional } from '../services/professional-admin.service';

const MIN_RATE = 1.5;
const MAX_RATE = 10;

/**
 * Editable per-minute rate in the admin professional sheet. A profile without
 * a rate cannot go online (and every call to it is refused), so the admin needs
 * a way to unblock it without going through the consultant's own editor.
 */
export function AdminRateField({ pro }: { pro: AdminProfessional }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(pro.costPerMinute != null ? String(pro.costPerMinute) : '');
  const mutation = useUpdateProfessionalRate();

  const parsed = Number(value.replace(',', '.'));
  const isValid = Number.isFinite(parsed) && parsed >= MIN_RATE && parsed <= MAX_RATE;

  const save = () => {
    if (!isValid) return;
    mutation.mutate(
      { id: pro.id, costPerMinute: parsed },
      { onSuccess: () => setEditing(false) }
    );
  };

  return (
    <div>
      <dt className="text-xs text-neutral-400">Tariffa/min</dt>
      {editing ? (
        <dd className="mt-0.5">
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              step="0.5"
              min={MIN_RATE}
              max={MAX_RATE}
              aria-label="Tariffa al minuto"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-24 rounded-lg border border-neutral-200 px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={save}
              disabled={!isValid || mutation.isPending}
              className="rounded-lg bg-brand-teal px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
            >
              Salva
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg border border-neutral-200 px-2.5 py-1 text-xs font-semibold text-neutral-600"
            >
              Annulla
            </button>
          </div>
          {!isValid && value !== '' && (
            <p className="mt-1 text-xs text-red-600">
              La tariffa deve essere tra €{MIN_RATE.toFixed(2)} e €{MAX_RATE.toFixed(2)}.
            </p>
          )}
        </dd>
      ) : (
        <dd className="mt-0.5 flex items-center gap-2 text-sm text-neutral-900">
          {pro.costPerMinute != null ? (
            `€${pro.costPerMinute.toFixed(2)}`
          ) : (
            <span className="text-xs font-semibold text-amber-700">
              Non impostata — il consulente non può andare online
            </span>
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs font-semibold text-brand-teal underline"
          >
            Modifica
          </button>
        </dd>
      )}
    </div>
  );
}
